import { findKpiForYear } from './meterFilters';

/**
 * Which meters a dashboard figure is calculated from.
 *
 * The summary cards ask the widget endpoint for a total by category, sub
 * category and KPI, and get back one number. Nothing on the card says how many
 * meters stand behind it, so 4,231 m³ from three healthy meters and 4,231 m³
 * from three meters with one of them dark look identical — and the second is
 * understated by whatever the dark one would have carried.
 *
 * No request is needed to answer this. The page already holds every meter with
 * its KPI mapping for the year, and the cards already state the KPI they are
 * asking for, so the match is a filter over data in hand.
 */

/**
 * Reads a metric's KPI filter, which is a name, several names, or absent.
 * @param {object} metric - One entry from WATER_METRICS.
 * @returns {Array<string>|null} The KPIs it selects, or null for all of them.
 */
const wantedKpis = (metric) =>
  metric?.kpi === undefined || metric?.kpi === null ? null : [].concat(metric.kpi);

/**
 * The meters whose mapping for the year matches what a card is asking for.
 *
 * The comparison mirrors the widget config the card builds: a field the metric
 * leaves out is one it does not filter on, so an absent category matches every
 * category rather than none.
 *
 * @param {object} metric - One entry from WATER_METRICS.
 * @param {Array<object>} meters - Meters as the list endpoint returns them.
 * @param {string} financialYear - Year whose mapping decides the match.
 * @returns {Array<{meterId: string, name: string, reporting: boolean}>} The
 *   contributors, in the order the meters arrived.
 */
export const metersBehind = (metric, meters = [], financialYear) => {
  const wanted = wantedKpis(metric);

  return (Array.isArray(meters) ? meters : [])
    .map((meter) => ({ meter, kpi: findKpiForYear(meter, financialYear) }))
    .filter(({ kpi }) => {
      if (!kpi) return false;
      if (metric?.category && kpi.category !== metric.category) return false;
      if (metric?.subCategory && kpi.subCategory !== metric.subCategory) return false;
      if (wanted && !wanted.includes(kpi.kpi)) return false;

      return true;
    })
    .map(({ meter }) => ({
      meterId: meter.meterId,
      name: meter.meterName || meter.meterId,
      reporting: !!meter.isActive,
    }));
};

/**
 * How many names fit on a card before the line stops being readable.
 *
 * Shibra alone maps to five meters, and five names wrap to three lines under a
 * figure that is two lines tall. Past this the count carries the fact and the
 * names move to the title attribute.
 */
const NAME_LIMIT = 3;

/**
 * States which meters a figure came from, and which of them are silent.
 *
 * A meter that is not reporting is named whatever the limit, because it is the
 * reason the figure is wrong and the only part the reader can act on. The
 * quiet ones are what get summarised away.
 *
 * @param {Array<object>} contributors - Output of `metersBehind`.
 * @returns {{summary: string, detail: string}|null} A line for the card and the
 *   full list for its tooltip, or null when nothing is mapped.
 */
export const describeContributors = (contributors = []) => {
  if (!contributors.length) return null;

  const silent = contributors.filter((c) => !c.reporting);
  const count = `${contributors.length} meter${contributors.length === 1 ? '' : 's'}`;
  const detail = contributors
    .map((c) => (c.reporting ? c.name : `${c.name} (not reporting)`))
    .join(', ');

  if (contributors.length <= NAME_LIMIT && !silent.length) {
    return { summary: `${count} · ${detail}`, detail };
  }

  if (!silent.length) return { summary: count, detail };

  const names = silent.map((c) => c.name).join(', ');
  return {
    summary: `${count} · ${names} not reporting`,
    detail,
  };
};
